import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { useAdminAuthStore } from "@/store/useAdminAuthStore";
import { useLogin } from "@/hooks/api/useAuthAPI";
import type { LoginCredentials } from "@/types";
import logo from "@/assets/images/bumpit-white-optimized.png";
import { Eye, EyeOff, User, Lock, AlertCircle } from "lucide-react";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const { isAuthenticated, setUser } = useAdminAuthStore();
  const loginMutation = useLogin();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>();

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (credentials: LoginCredentials) => {
    try {
      const response = await loginMutation.mutateAsync(credentials);

      // Update Zustand store with user data
      setUser(response.data.admin);

      // Navigate to dashboard
      navigate("/dashboard", { replace: true });
    } catch (error) {
      // Error already logged in the hook
      console.error("Login failed:", error);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-dark-5 via-dark-10 to-dark-20 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md">
        {/* Login Card */}
        <div className="bg-dark-20 backdrop-blur-xl border border-dark-40 rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            {/* Logo/Brand */}
            <div className="inline-flex justify-center mb-6 w-full">
              <img src={logo} alt="bumpit" className="h-12" />
            </div>

            <p className="text-dark-100 text-base">
              Sign in to access your admin dashboard
            </p>
          </div>

          {/* Error Alert */}
          {loginMutation.isError && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
              <span className="text-error text-sm">
                {loginMutation.error instanceof Error
                  ? loginMutation.error.message
                  : "Login failed. Please try again."}
              </span>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-dark-text mb-2"
              >
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-dark-80" />
                </div>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={`
                    block w-full pl-10 pr-3 py-3 
                    bg-dark-30 border rounded-lg
                    text-dark-text placeholder-dark-80
                    focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
                    transition-all duration-200
                    ${
                      errors.email
                        ? "border-error focus:ring-error/50 focus:border-error"
                        : "border-dark-50 hover:border-dark-60"
                    }
                  `}
                  placeholder="Enter your email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-error">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-dark-text mb-2"
              >
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-dark-80" />
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className={`
                    block w-full pl-10 pr-10 py-3 
                    bg-dark-30 border rounded-lg
                    text-dark-text placeholder-dark-80
                    focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
                    transition-all duration-200
                    ${
                      errors.password
                        ? "border-error focus:ring-error/50 focus:border-error"
                        : "border-dark-50 hover:border-dark-60"
                    }
                  `}
                  placeholder="Enter your password"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-dark-80 hover:text-dark-text transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-dark-80 hover:text-dark-text transition-colors" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-error">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="
                w-full flex justify-center py-3 px-4 cursor-pointer
                bg-dark-text
                hover:bg-dark-100
                disabled:from-dark-50 disabled:to-dark-50
                border border-transparent rounded-lg 
                text-dark-5 font-medium text-base
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:ring-offset-dark-20
                transition-all duration-200
                disabled:cursor-not-allowed
                transform hover:scale-[1.02] active:scale-[0.98]
                shadow-lg hover:shadow-xl
              "
            >
              {loginMutation.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-dark-40">
            <p className="text-center text-sm text-dark-100">
              Bumpit Admin Dashboard
            </p>
            <p className="text-center text-xs text-dark-80 mt-1">
              {import.meta.env.VITE_APP_VERSION || "v1.0.0"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
